/**
 * Battlefield terrain generation — v0M4.0j.1 (BATTLES.md §2.8.6).
 *
 * Rolls a 20×20 terrain grid + feature list from a strategic-map cue +
 * `battle.rng`. Called once at `createBattleFromArmies` time and frozen
 * for the battle's duration per §2.8.6.7 determinism rule.
 *
 * The generator is deterministic — same seed + cue → same output. MP-
 * safe by construction: no `Math.random`, no clock reads.
 *
 * Pipeline:
 *   1. Convert strategic terrain id → battlescape cue TerrainType (§2.8.6.2 step 2).
 *   2. Fill 20×20 cells: 60% cue / 30% Grass / 10% mixed variants (§2.8.6.2 step 3).
 *   3. Feature placement in order — river crossing → forest patches →
 *      hills clusters → fortification (§2.8.6.2 step 4).
 *   4. Passability audit: guarantee a path from side-0 deployment to
 *      side-1 deployment. If broken, overwrite row 10 with Grass (§2.8.6.2 step 5).
 *
 * Terrain layout convention: `terrain[y * BATTLESCAPE_WIDTH + x]` — row-
 * major flat array, y = 0 is the NORTH edge (side 1's deployment), y =
 * BATTLESCAPE_HEIGHT-1 is the SOUTH edge (side 0's deployment). Matches
 * `battle-factory.ts` layoutSquares row indexing.
 */

import {
  BATTLESCAPE_CELL_COUNT,
  BATTLESCAPE_HEIGHT,
  BATTLESCAPE_WIDTH,
  TERRAIN_TYPE_ORDER,
} from './battle-state';
import type { TerrainFeature, TerrainType } from './battle-state';
import { nextInt } from './rng';
import type { RngState } from './rng';
import { getDialNumber, loadDials } from '../data/dials';

// MFix2.5 — per-feature cluster + river dials. Loaded at module init.
const TERRAIN_DIALS = loadDials();
const FOREST_PATCH_SIZE = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_forest_patch_size')));
const HILLS_CLUSTER_SIZE = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_hills_cluster_size')));
const MARSH_PATCH_SIZE = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_marsh_patch_size')));
const MAJOR_RIVER_BRIDGES = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_river_major_bridges')));
const SMALL_RIVER_LEN_MIN = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_river_small_length_min')));
const SMALL_RIVER_LEN_MAX = Math.max(SMALL_RIVER_LEN_MIN, Math.round(getDialNumber(TERRAIN_DIALS, 'battle_terrain_river_small_length_max')));

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Impassable terrain types — cohorts cannot enter these cells per
 * §2.8.6.1. Water and Mountains block movement; every other type is
 * passable (with cavalry-slow interaction on Hills/Marsh/Forest per
 * §2.8.6.3 handled by the movement code, not here).
 */
export function isPassable(t: TerrainType): boolean {
  return t !== 'Water' && t !== 'Mountains';
}

/**
 * Slow-terrain flag per §2.8.6.3 — Cavalry cohorts have their per-round
 * step budget clamped to 1 when standing on one of these types.
 */
export function isCavalrySlow(t: TerrainType): boolean {
  return t === 'Hills' || t === 'Forest' || t === 'Swamp';
}

/**
 * v0M4.0k.6 (2026-07-27) — Wall / Gate outward facing helper.
 *
 * A Wall or Gate cell "faces outward" — the cardinal direction pointing
 * AWAY from the settlement interior toward the attacking side. Derived
 * from perimeter analysis of the local 4-neighbourhood:
 *
 *   - Count Wall/Gate neighbours in each of the 4 cardinal directions.
 *   - The "outside" direction is the cardinal side with the fewest
 *     Wall/Gate neighbours (i.e. the side with open ground).
 *   - Ties break N > S > E > W (arbitrary but deterministic).
 *
 * This is used by the renderer (crenellation faces outward, arch orients
 * outward) and available to any future feature that wants directional
 * cover. The combat matrix does NOT gate on facing per Michael's design
 * lock — matrix modifier applies regardless of attacker side.
 *
 * Returns null if (x, y) is out of bounds or the cell isn't a Wall/Gate.
 * Returns null if the perimeter is degenerate (all 4 neighbours are
 * Wall/Gate — e.g. an interior wall block; shouldn't happen in normal
 * templates).
 */
export function computeWallOrGateFacing(
  terrain: Uint8Array,
  x: number,
  y: number,
): 'N' | 'S' | 'E' | 'W' | null {
  const here = terrainTypeAt(terrain, x, y);
  if (here !== 'Wall' && here !== 'Gate') return null;

  // Count Wall/Gate presence in each cardinal direction. 1 = wall/gate
  // neighbour (or off-map, treated as inside so we don't face out into
  // the void), 0 = open.
  const northN = neighbourIsInside(terrain, x, y - 1);
  const southN = neighbourIsInside(terrain, x, y + 1);
  const eastN  = neighbourIsInside(terrain, x + 1, y);
  const westN  = neighbourIsInside(terrain, x - 1, y);

  // Find the direction with the LOWEST inside-score (i.e. the open side).
  // Tie order: N > S > E > W.
  const options: [ 'N' | 'S' | 'E' | 'W', boolean ][] = [
    ['N', northN], ['S', southN], ['E', eastN], ['W', westN],
  ];
  // Prefer a direction where the neighbour is NOT inside (i.e. open).
  const open = options.filter(([, inside]) => !inside);
  if (open.length === 0) return null; // Fully enclosed — degenerate.
  return open[0]![0];
}

/**
 * True if (x, y) is a Wall or Gate cell, OR out of bounds (treated as
 * "inside" so perimeter walls at the edge of the battlefield face
 * inward toward the interior rather than out into off-map void).
 */
function neighbourIsInside(terrain: Uint8Array, x: number, y: number): boolean {
  if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) return true;
  const t = terrainTypeAt(terrain, x, y);
  return t === 'Wall' || t === 'Gate';
}

/** Lookup a terrain type at cell (x, y) from a battle's terrain grid. */
export function terrainTypeAt(terrain: Uint8Array, x: number, y: number): TerrainType {
  if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) {
    // Off-grid cells are treated as impassable (Mountains ordinal) — the
    // sim never actually queries out-of-bounds but this is the safe answer.
    return 'Mountains';
  }
  const ord = terrain[y * BATTLESCAPE_WIDTH + x]!;
  return TERRAIN_TYPE_ORDER[ord] ?? 'Plains';
}

// ---------------------------------------------------------------------------
// Cue mapping — strategic terrain id → battlescape cue TerrainType
// ---------------------------------------------------------------------------

/**
 * Strategic terrain-id vocabulary from `pathfind.ts` TERRAIN_COST:
 *   0 Plains / 1 Farmland / 2 Forest / 3 Hills / 4 Mountains /
 *   5 Desert / 6 Swamp / 7 Lake / 8 Sea
 * Mapping per BATTLES.md §2.8.6.2 step 2.
 */
export function strategicCueToBattlescape(strategicTerrainId: number): TerrainType {
  switch (strategicTerrainId) {
    case 0: return 'Plains';       // Plains
    case 1: return 'Plains';       // Farmland
    case 2: return 'Forest';
    case 3: return 'Hills';
    case 4: return 'Mountains';
    case 5: return 'Plains';       // Desert — Rocky/Sand deferred, roll on Grass
    case 6: return 'Swamp';       // Swamp → Marsh
    case 7: return 'Water';       // Lake
    case 8: return 'Water';       // Sea
    default: return 'Plains';
  }
}

// ---------------------------------------------------------------------------
// Generator
// ---------------------------------------------------------------------------

/**
 * Generator input options. `cue` is the strategic-map terrain type sampled
 * at the ZoC midpoint. `hasNearbyRiver` + `hasNearbyForest` + `hasNearbyHills`
 * flags indicate whether the strategic-map terrain around the midpoint has
 * those features (feature seeding uses them per §2.8.6.2 step 4). Setting
 * `nearFriendlyCity` enables Fortification placement.
 */
export interface GenerateBattleTerrainOptions {
  readonly cue: TerrainType;
  readonly hasNearbyRiver?: boolean;
  readonly hasNearbyForest?: boolean;
  readonly hasNearbyHills?: boolean;
  readonly nearFriendlyCity?: boolean;
}

export interface GeneratedTerrain {
  readonly terrain: Uint8Array;
  readonly features: TerrainFeature[];
}

/**
 * Generate a battlefield terrain grid + feature list. Mutates `rng` per
 * the sim's standard RNG-threading convention. Returns a fresh Uint8Array
 * of length `BATTLESCAPE_CELL_COUNT` + a feature list keyed to that grid.
 */
export function generateBattleTerrain(
  rng: RngState,
  options: GenerateBattleTerrainOptions,
): GeneratedTerrain {
  const terrain = new Uint8Array(BATTLESCAPE_CELL_COUNT);
  const cue = options.cue;

  // Step 1 — 60/30/10 base fill.
  //   60% cue-type OR its passable substitute (Water cue → Grass base fill
  //   with the actual river painted at feature-placement time).
  //   30% Grass baseline.
  //   10% mixed variants — weighted by cue toward reinforcing the cue's flavour.
  const baseFillCue = cue === 'Water' || cue === 'Mountains' ? 'Plains' : cue;
  const mixedWeights = mixedVariantWeightsFor(cue);

  for (let y = 0; y < BATTLESCAPE_HEIGHT; y++) {
    for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
      const r = nextInt(rng, 0, 100);
      let t: TerrainType;
      if (r < 60) t = baseFillCue;
      else if (r < 90) t = 'Plains';
      else t = pickWeighted(rng, mixedWeights);
      terrain[y * BATTLESCAPE_WIDTH + x] = ordinalOf(t);
    }
  }

  const features: TerrainFeature[] = [];

  // Step 2 — feature placement in fixed order (deterministic).

  // (a) River — MFix2.5 differentiates by cue:
  //   * cue === 'Water' → major edge-to-edge river with N bridges (chokepoint drama).
  //   * hasNearbyRiver only → small 5-9 tile river randomly located (may or may not have a bridge).
  if (cue === 'Water') {
    placeMajorRiver(rng, terrain, features);
  } else if (options.hasNearbyRiver) {
    placeSmallRiver(rng, terrain, features);
  }

  // (b) Forest patches — 2–3 clusters targeting the forest-patch-size dial
  //     (default 7 tiles per patch). Seeded when cue is Forest or a nearby
  //     forest was flagged. MFix2.5: switched from radius-blob math to
  //     target-cell-count random-walk fill (more predictable size).
  if (cue === 'Forest' || options.hasNearbyForest) {
    const patchCount = 2 + nextInt(rng, 0, 2); // 2 or 3
    placeTargetSizePatches(rng, terrain, features, 'Forest', 'ForestPatch', patchCount, FOREST_PATCH_SIZE);
  }

  // (c) Hills clusters — 1–2 clusters targeting the hills-cluster-size dial
  //     (default 7 tiles). Seeded when cue is Hills or a nearby hills was flagged.
  if (cue === 'Hills' || options.hasNearbyHills) {
    const clusterCount = 1 + nextInt(rng, 0, 2); // 1 or 2
    placeTargetSizePatches(rng, terrain, features, 'Hills', 'HillsCluster', clusterCount, HILLS_CLUSTER_SIZE);
  }

  // (c') Marsh patches — MFix2.5: added explicit marsh patch placement.
  //     1-2 patches targeting the marsh-patch-size dial (default 6 tiles).
  //     Seeded on Marsh cue; hasNearbyMarsh flag doesn't exist yet so
  //     off-cue only fires from mixed-variant fill.
  if (cue === 'Swamp') {
    const patchCount = 1 + nextInt(rng, 0, 2); // 1 or 2
    placeTargetSizePatches(rng, terrain, features, 'Swamp', 'ForestPatch', patchCount, MARSH_PATCH_SIZE);
  }

  // (d) Mountains cue — sprinkle Mountains cells to make the passable
  //     corridor feel Mountains-heavy. 5-15 cells at rolled positions.
  //     Distinct from the Mountains-in-mixed-variant roll — this guarantees
  //     the visual signature of a mountains battle.
  if (cue === 'Mountains') {
    const mountainSprinkle = 8 + nextInt(rng, 0, 8); // 8-15
    for (let i = 0; i < mountainSprinkle; i++) {
      const x = nextInt(rng, 0, BATTLESCAPE_WIDTH);
      const y = nextInt(rng, 0, BATTLESCAPE_HEIGHT);
      // Only overwrite if current cell is Grass or Hills — don't stomp
      // rivers / bridges / forest patches from earlier feature passes.
      const current = terrainTypeAt(terrain, x, y);
      if (current === 'Plains' || current === 'Hills') {
        terrain[y * BATTLESCAPE_WIDTH + x] = ordinalOf('Mountains');
      }
    }
  }

  // (e) Fortification — 1-2 cells, only if a friendly city is nearby.
  if (options.nearFriendlyCity) {
    const fortCount = 1 + nextInt(rng, 0, 2); // 1 or 2
    placeFortification(rng, terrain, features, fortCount);
  }

  // Step 3 — passability audit. Guarantee a path from side 0's deployment
  //   zone (bottom rows) to side 1's (top rows). If no path exists, force
  //   Grass across row 10 (mid-battlescape) — same seed still deterministic.
  if (!auditPassablePath(terrain)) {
    for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
      terrain[10 * BATTLESCAPE_WIDTH + x] = ordinalOf('Plains');
    }
  }

  return { terrain, features };
}

// ---------------------------------------------------------------------------
// Feature placement helpers
// ---------------------------------------------------------------------------

/**
 * MFix2.5 — Major river: horizontal Water strip at row 10 spanning the
 * full width with N Bridge cells (default 3 per `battle_terrain_river_major_bridges`).
 * Fires when the strategic-map cue is Water (battle is directly over a
 * river). Bridges deterministically distributed across the width to
 * avoid clumping — divides row into (N+1) sections and places one
 * bridge in each interior division with small jitter.
 */
function placeMajorRiver(
  rng: RngState,
  terrain: Uint8Array,
  features: TerrainFeature[],
): void {
  const midRow = 10;
  const waterCells: [number, number][] = [];
  for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
    terrain[midRow * BATTLESCAPE_WIDTH + x] = ordinalOf('Water');
    waterCells.push([x, midRow]);
  }
  features.push({ type: 'RiverStrip', cells: waterCells });

  // Distribute N bridges evenly across the width with small jitter.
  const bridgeCount = MAJOR_RIVER_BRIDGES;
  const sectionWidth = BATTLESCAPE_WIDTH / (bridgeCount + 1);
  const bridgeCells: [number, number][] = [];
  const usedColumns = new Set<number>();
  for (let i = 1; i <= bridgeCount; i++) {
    const anchorX = Math.round(i * sectionWidth);
    // ±1 jitter for organic feel; clamp to [1, W-2].
    const jitter = nextInt(rng, 0, 3) - 1;
    let bridgeX = Math.max(1, Math.min(BATTLESCAPE_WIDTH - 2, anchorX + jitter));
    // De-dup — if two bridges land on the same column, shift one over.
    while (usedColumns.has(bridgeX)) {
      bridgeX = Math.max(1, Math.min(BATTLESCAPE_WIDTH - 2, bridgeX + 1));
      if (usedColumns.size >= BATTLESCAPE_WIDTH - 2) break; // Safety
    }
    usedColumns.add(bridgeX);
    terrain[midRow * BATTLESCAPE_WIDTH + bridgeX] = ordinalOf('Bridge');
    bridgeCells.push([bridgeX, midRow]);
  }
  features.push({ type: 'Bridge', cells: bridgeCells });
}

/**
 * MFix2.5 — Small river: a 5-9 tile straight-line water strip at a
 * random interior location, oriented horizontally or vertically. Fires
 * when hasNearbyRiver flag is set but cue !== 'Water' (a river was
 * spotted in the strategic-map area around the ZoC midpoint but the
 * battle isn't directly on it). No bridge — small rivers are terrain
 * decoration, not chokepoint drama.
 */
function placeSmallRiver(
  rng: RngState,
  terrain: Uint8Array,
  features: TerrainFeature[],
): void {
  const length = SMALL_RIVER_LEN_MIN + nextInt(rng, 0, SMALL_RIVER_LEN_MAX - SMALL_RIVER_LEN_MIN + 1);
  const horizontal = nextInt(rng, 0, 2) === 0;
  const cells: [number, number][] = [];
  if (horizontal) {
    // Start row in [4, 15] (avoid deployment rows to keep passable start).
    const row = 4 + nextInt(rng, 0, 12);
    const startX = 1 + nextInt(rng, 0, Math.max(1, BATTLESCAPE_WIDTH - length - 2));
    for (let i = 0; i < length; i++) {
      const x = Math.min(BATTLESCAPE_WIDTH - 1, startX + i);
      terrain[row * BATTLESCAPE_WIDTH + x] = ordinalOf('Water');
      cells.push([x, row]);
    }
  } else {
    // Vertical — cols in [1, 18] (avoid extreme edges).
    const col = 1 + nextInt(rng, 0, BATTLESCAPE_WIDTH - 2);
    const startY = 3 + nextInt(rng, 0, Math.max(1, BATTLESCAPE_HEIGHT - length - 5));
    for (let i = 0; i < length; i++) {
      const y = Math.min(BATTLESCAPE_HEIGHT - 1, startY + i);
      terrain[y * BATTLESCAPE_WIDTH + col] = ordinalOf('Water');
      cells.push([col, y]);
    }
  }
  features.push({ type: 'RiverStrip', cells });
}

/**
 * MFix2.5 — Seed `count` patches of `terrainType`. Each patch targets
 * approximately `targetCellCount` cells (default 6-8 per Michael's
 * playtest spec), grown by BFS-outward random-walk fill from a rolled
 * centre. Produces organic cluster shapes with predictable size —
 * replaces the pre-MFix2.5 radius-blob math which was hard to tune.
 *
 * Overwrites whatever was there (including Water/Mountains). Cluster
 * size may fall short of target if the seed hits a small unreachable
 * corner (rare on 20×20).
 */
function placeTargetSizePatches(
  rng: RngState,
  terrain: Uint8Array,
  features: TerrainFeature[],
  terrainType: TerrainType,
  featureType: TerrainFeature['type'],
  count: number,
  targetCellCount: number,
): void {
  for (let p = 0; p < count; p++) {
    const cx = nextInt(rng, 0, BATTLESCAPE_WIDTH);
    const cy = nextInt(rng, 0, BATTLESCAPE_HEIGHT);
    const cells: [number, number][] = [];
    const placed = new Set<number>();
    // Frontier BFS with random neighbour shuffle for organic growth.
    const frontier: [number, number][] = [[cx, cy]];
    while (cells.length < targetCellCount && frontier.length > 0) {
      // Deterministic-random pick from the frontier — pop a random index.
      const idx = nextInt(rng, 0, frontier.length);
      const [x, y] = frontier[idx]!;
      frontier.splice(idx, 1);
      if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) continue;
      const key = y * BATTLESCAPE_WIDTH + x;
      if (placed.has(key)) continue;
      placed.add(key);
      terrain[key] = ordinalOf(terrainType);
      cells.push([x, y]);
      // Push 4-cardinal neighbours onto the frontier (shuffled by frontier-pop random-idx above).
      frontier.push([x + 1, y]);
      frontier.push([x - 1, y]);
      frontier.push([x, y + 1]);
      frontier.push([x, y - 1]);
    }
    if (cells.length > 0) {
      features.push({ type: featureType, cells });
    }
  }
}

/**
 * Place 1–2 Fortification cells at rolled positions. Overwrites Grass /
 * Hills only — doesn't stomp river / bridge / forest features.
 */
function placeFortification(
  rng: RngState,
  terrain: Uint8Array,
  features: TerrainFeature[],
  count: number,
): void {
  const cells: [number, number][] = [];
  let placed = 0;
  let attempts = 0;
  while (placed < count && attempts < 30) {
    attempts++;
    const x = nextInt(rng, 0, BATTLESCAPE_WIDTH);
    const y = nextInt(rng, 0, BATTLESCAPE_HEIGHT);
    const current = terrainTypeAt(terrain, x, y);
    if (current !== 'Plains' && current !== 'Hills') continue;
    terrain[y * BATTLESCAPE_WIDTH + x] = ordinalOf('Wall');
    cells.push([x, y]);
    placed++;
  }
  if (cells.length > 0) {
    features.push({ type: 'Wall', cells });
  }
}

// ---------------------------------------------------------------------------
// Passability audit
// ---------------------------------------------------------------------------

/**
 * BFS from row 15-19 (side-0 deployment) trying to reach row 0-4 (side-1
 * deployment). Returns true iff a passable path exists on the current
 * terrain. Ignores cohort occupancy — only queries terrain passability.
 */
function auditPassablePath(terrain: Uint8Array): boolean {
  const visited = new Uint8Array(BATTLESCAPE_CELL_COUNT);
  const queue: number[] = [];

  for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
    for (let y = BATTLESCAPE_HEIGHT - 5; y < BATTLESCAPE_HEIGHT; y++) {
      const i = y * BATTLESCAPE_WIDTH + x;
      if (isPassable(TERRAIN_TYPE_ORDER[terrain[i]!]!)) {
        queue.push(i);
        visited[i] = 1;
      }
    }
  }

  while (queue.length > 0) {
    const i = queue.shift()!;
    const x = i % BATTLESCAPE_WIDTH;
    const y = Math.floor(i / BATTLESCAPE_WIDTH);
    if (y < 5) return true; // Reached side-1 deployment rows.
    // 4-neighbour expansion — cardinal only for the audit (bridges are
    // cardinal-passable by construction; no diagonal cheating).
    const neighbours: [number, number][] = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ];
    for (const [nx, ny] of neighbours) {
      if (nx < 0 || nx >= BATTLESCAPE_WIDTH || ny < 0 || ny >= BATTLESCAPE_HEIGHT) continue;
      const ni = ny * BATTLESCAPE_WIDTH + nx;
      if (visited[ni]) continue;
      if (!isPassable(TERRAIN_TYPE_ORDER[terrain[ni]!]!)) continue;
      visited[ni] = 1;
      queue.push(ni);
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// v0M4.2.3 — Settlement-siege battle terrain generator (BATTLES.md §3.6)
// ---------------------------------------------------------------------------

/**
 * Load `wall_hp` dial once at module init — used both for the initial
 * populate of `Battle.wallHp` and for validation in tests.
 */
export const WALL_HP_INITIAL = Math.max(1, Math.round(getDialNumber(TERRAIN_DIALS, 'wall_hp')));

/**
 * v0M4.2.4 — Fraction of a firer's `missileAttack` stat that lands on a
 * wall per shot. Catapult (missileAttack 80) × 0.5 = 40 damage/shot;
 * default `wall_hp` (100) breaches in ~3 shots. Tuneable via first
 * playtest. Deterministic (no RNG) so a siege battle re-plays identically
 * across MP hosts.
 */
export const WALL_DAMAGE_PER_SHOT_MULTIPLIER = 0.5;

/**
 * v0M4.2.4 — Apply `damage` HP to the wall cell at `cellIndex`. If HP
 * falls to 0 or below, convert the cell to Grass rubble (BATTLES.md §3.7):
 *   - Rewrites `battle.terrain[cellIndex]` from Fortification → Grass.
 *   - Pushes a `Rubble` TerrainFeature so the renderer can overlay a
 *     rubble-pile glyph and audit tools can see the conversion.
 *   - Removes the entry from `battle.wallHp` (both signals that the wall
 *     is gone; downstream code checks `wallHp.has(idx)` to know if a
 *     cell is still a "living wall").
 * Returns `{ breached: boolean, remainingHp: number }`.
 *
 * Silent no-op if `cellIndex` isn't a live wall (defensive — shouldn't
 * happen given caller checks, but safe).
 */
export function damageWall(
  battle: { terrain: Uint8Array; wallHp: Map<number, number>; terrainFeatures: TerrainFeature[] },
  cellIndex: number,
  damage: number,
): { breached: boolean; remainingHp: number } {
  const currentHp = battle.wallHp.get(cellIndex);
  if (currentHp === undefined) return { breached: false, remainingHp: 0 };
  const nextHp = currentHp - damage;
  if (nextHp <= 0) {
    // Breach — convert to Grass rubble.
    battle.terrain[cellIndex] = ordinalOf('Plains');
    battle.wallHp.delete(cellIndex);
    const x = cellIndex % BATTLESCAPE_WIDTH;
    const y = Math.floor(cellIndex / BATTLESCAPE_WIDTH);
    battle.terrainFeatures.push({ type: 'Rubble', cells: [[x, y]] });
    return { breached: true, remainingHp: 0 };
  }
  battle.wallHp.set(cellIndex, nextHp);
  return { breached: false, remainingHp: nextHp };
}

/**
 * v0M4.2.4 — True iff `(x, y)` sits on an active wall cell in this
 * battle. Consumed by movement code to enforce wall impassability
 * (BATTLES.md §3.6 / §3.7). Once the wall breaches, `wallHp` no longer
 * contains the entry, so this returns false and cohorts can walk
 * through the rubble.
 */
export function isWallCell(battle: { wallHp: Map<number, number> }, x: number, y: number): boolean {
  if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) return false;
  return battle.wallHp.has(y * BATTLESCAPE_WIDTH + x);
}

/**
 * Wall layout constants per BATTLES.md §3.6. The defender occupies the
 * bottom half of the 20×20 battlescape (higher y), backed against the
 * defender edge (y = 19). Walls form a U-shape open toward the defender
 * edge: front wall (row 12 across), left wall (column 0), right wall
 * (column 19), no rear wall.
 *
 * Gate positions are the walkable holes in each wall — Bridge-tile
 * equivalents. Attackers must enter through a gate (v0M4.2.3 — walls
 * are impassable; v0M4.2.4 makes them breachable).
 */
export const SIEGE_FRONT_WALL_ROW = 12;
export const SIEGE_FRONT_GATE_X = 10;
export const SIEGE_LEFT_WALL_COL = 0;
export const SIEGE_RIGHT_WALL_COL = BATTLESCAPE_WIDTH - 1;
export const SIEGE_SIDE_GATE_Y = 15;

/** Shape returned by `generateSiegeBattleTerrain` — same shape as `GeneratedTerrain` plus the wall-cell list. */
export interface GeneratedSiegeTerrain {
  readonly terrain: Uint8Array;
  readonly features: TerrainFeature[];
  /**
   * Terrain-cell indices (y * BATTLESCAPE_WIDTH + x) of every Fortification
   * wall cell placed by the generator. Used by `battle-factory.ts` to
   * seed `Battle.wallHp` at `WALL_HP_INITIAL`. Iteration order matches
   * placement order (front row → left column → right column).
   */
  readonly wallCells: readonly number[];
}

/**
 * v0M4.2.3 — Generate a city-siege battlefield terrain grid per BATTLES.md
 * §3.6. Layout:
 *   - Rows 0-11 (attacker half): standard field-battle terrain scatter,
 *     sampled from the strategic-cell cue at the city.
 *   - Row 12: Fortification wall spanning cols 0-19, with a Bridge gate
 *     at (SIEGE_FRONT_GATE_X, 12).
 *   - Col 0, rows 12-19: Fortification wall down the left edge, with a
 *     Bridge gate at (0, SIEGE_SIDE_GATE_Y).
 *   - Col 19, rows 12-19: Fortification wall down the right edge, with a
 *     Bridge gate at (19, SIEGE_SIDE_GATE_Y).
 *   - Interior (rows 13-18, cols 1-18): Grass — clean defender deploy zone.
 *   - Rear edge (row 19): no wall (defender's back is the map boundary).
 *
 * Determinism: mutates `rng` per the sim's RNG-threading rule; same seed +
 * options → same terrain. Wall + gate positions are constants — only the
 * attacker-half scatter varies with rng.
 */
export function generateSiegeBattleTerrain(
  rng: RngState,
  options: GenerateBattleTerrainOptions,
): GeneratedSiegeTerrain {
  // Step 1 — generate the attacker half using the standard generator.
  // (Field-battle scatter fills the whole 20×20; we then overwrite the
  // defender half with clean Grass + the wall layout.)
  const { terrain, features } = generateBattleTerrain(rng, options);

  // Step 2 — reset rows 12-19 to Grass baseline. Features intersecting
  // the defender half are dropped so the wall-cell rendering doesn't
  // collide with a stale ForestPatch / HillsCluster overlay.
  for (let y = SIEGE_FRONT_WALL_ROW; y < BATTLESCAPE_HEIGHT; y++) {
    for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
      terrain[y * BATTLESCAPE_WIDTH + x] = ordinalOf('Plains');
    }
  }
  const filteredFeatures = features.filter((f) => {
    for (const [_x, y] of f.cells) {
      if (y >= SIEGE_FRONT_WALL_ROW) return false;
    }
    return true;
  });

  // Step 3 — walls + gates. Track wall cells for wallHp seeding.
  const wallCells: number[] = [];
  const wallCellPositions: [number, number][] = [];
  const gateCellPositions: [number, number][] = [];

  // Front wall — row 12 cols 0-19, minus the front gate cell.
  for (let x = 0; x < BATTLESCAPE_WIDTH; x++) {
    if (x === SIEGE_FRONT_GATE_X) continue; // Gate cell — placed below.
    terrain[SIEGE_FRONT_WALL_ROW * BATTLESCAPE_WIDTH + x] = ordinalOf('Wall');
    const idx = SIEGE_FRONT_WALL_ROW * BATTLESCAPE_WIDTH + x;
    wallCells.push(idx);
    wallCellPositions.push([x, SIEGE_FRONT_WALL_ROW]);
  }
  terrain[SIEGE_FRONT_WALL_ROW * BATTLESCAPE_WIDTH + SIEGE_FRONT_GATE_X] = ordinalOf('Bridge');
  gateCellPositions.push([SIEGE_FRONT_GATE_X, SIEGE_FRONT_WALL_ROW]);

  // Left wall — col 0 rows 13-18, minus the left gate cell. Stops at
  // row 18 so row 19 (defender-edge / open rear) stays Grass per §3.6.
  for (let y = SIEGE_FRONT_WALL_ROW + 1; y < BATTLESCAPE_HEIGHT - 1; y++) {
    if (y === SIEGE_SIDE_GATE_Y) continue;
    terrain[y * BATTLESCAPE_WIDTH + SIEGE_LEFT_WALL_COL] = ordinalOf('Wall');
    const idx = y * BATTLESCAPE_WIDTH + SIEGE_LEFT_WALL_COL;
    wallCells.push(idx);
    wallCellPositions.push([SIEGE_LEFT_WALL_COL, y]);
  }
  terrain[SIEGE_SIDE_GATE_Y * BATTLESCAPE_WIDTH + SIEGE_LEFT_WALL_COL] = ordinalOf('Bridge');
  gateCellPositions.push([SIEGE_LEFT_WALL_COL, SIEGE_SIDE_GATE_Y]);

  // Right wall — col 19 rows 13-18, minus the right gate cell. Same
  // row 19 exclusion as the left wall.
  for (let y = SIEGE_FRONT_WALL_ROW + 1; y < BATTLESCAPE_HEIGHT - 1; y++) {
    if (y === SIEGE_SIDE_GATE_Y) continue;
    terrain[y * BATTLESCAPE_WIDTH + SIEGE_RIGHT_WALL_COL] = ordinalOf('Wall');
    const idx = y * BATTLESCAPE_WIDTH + SIEGE_RIGHT_WALL_COL;
    wallCells.push(idx);
    wallCellPositions.push([SIEGE_RIGHT_WALL_COL, y]);
  }
  terrain[SIEGE_SIDE_GATE_Y * BATTLESCAPE_WIDTH + SIEGE_RIGHT_WALL_COL] = ordinalOf('Bridge');
  gateCellPositions.push([SIEGE_RIGHT_WALL_COL, SIEGE_SIDE_GATE_Y]);

  // Step 4 — append semantic features for the walls + gates (used by
  // the renderer to overlay the crenellated-block + bridge glyphs and
  // by audit tools).
  filteredFeatures.push({ type: 'Wall', cells: wallCellPositions });
  filteredFeatures.push({ type: 'Bridge', cells: gateCellPositions });

  return { terrain, features: filteredFeatures, wallCells };
}

// ---------------------------------------------------------------------------
// Internal utilities
// ---------------------------------------------------------------------------

/** Ordinal index of a TerrainType within TERRAIN_TYPE_ORDER (constant-time table lookup). */
function ordinalOf(t: TerrainType): number {
  // TERRAIN_TYPE_ORDER is small (8 entries); linear scan is fine + inlineable.
  for (let i = 0; i < TERRAIN_TYPE_ORDER.length; i++) {
    if (TERRAIN_TYPE_ORDER[i] === t) return i;
  }
  return 0; // Defensive — Grass fallback for unknown types.
}

/**
 * Weights per cue for the 10%-mixed variant slot. Reinforces cue-flavour
 * while occasionally sprinkling other passable types for visual variety.
 * All weights sum to 1.0 within each cue.
 */
type WeightedTerrain = readonly (readonly [TerrainType, number])[];
function mixedVariantWeightsFor(cue: TerrainType): WeightedTerrain {
  switch (cue) {
    case 'Plains':
      return [['Hills', 0.4], ['Forest', 0.3], ['Swamp', 0.2], ['Water', 0.1]];
    case 'Hills':
      return [['Hills', 0.5], ['Plains', 0.3], ['Mountains', 0.15], ['Forest', 0.05]];
    case 'Forest':
      return [['Forest', 0.5], ['Plains', 0.3], ['Swamp', 0.15], ['Hills', 0.05]];
    case 'Swamp':
      return [['Swamp', 0.5], ['Plains', 0.25], ['Water', 0.2], ['Forest', 0.05]];
    case 'Mountains':
      return [['Mountains', 0.4], ['Hills', 0.4], ['Plains', 0.2]];
    case 'Water':
      return [['Plains', 0.6], ['Swamp', 0.3], ['Forest', 0.1]];
    case 'Bridge':
    case 'Wall':
    default:
      return [['Plains', 1.0]];
  }
}

/** Pick a terrain type by weight; determinism via `nextInt(rng, 0, 100)`. */
function pickWeighted(rng: RngState, weights: WeightedTerrain): TerrainType {
  const r = nextInt(rng, 0, 100) / 100;
  let acc = 0;
  for (const [t, w] of weights) {
    acc += w;
    if (r < acc) return t;
  }
  return weights[weights.length - 1]![0];
}
